/**
 * Page-local artifact-panel state (no store). Holds the open artifact, docked/expanded mode, and the
 * element that opened it so focus can return there on close. The artifact is scoped to the thread it was opened
 * on: switching threads (the panel lives outside ChatBox's navKey remount) derives it closed, since the opening
 * card belongs to the previous thread. "New chat" derives it closed too, whatever it is scoped to.
 * `ArtifactPanelContext` exposes just `openArtifact` to the inline cards,
 * which render deep inside `<ChatBox>` and can't be handed a callback prop directly.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useLatest } from "./use-latest"
import { type ArtifactEnvelope, deliverableOf, isResourceAddressedArtifact } from "./artifact-contract"
import { historyIndex, KellyLinkContext, kellyLinkOf, kellyStartIndex } from "../kelly-link-types"
import type { KellyLink } from "../kelly-link-types"
import { hasPermanentDrawer, useChatLayout } from "./chat-layout-context"

/** A ref whose `current` we only read + `focus()` — `readonly` so a `RefObject<HTMLDivElement | null>` fits. */
interface FocusFallbackRef {
  readonly current: HTMLElement | null
}

/**
 * How the panel came to be showing this artifact — a click on a card, a shared artifact's URL, a
 * workflow report opened from the Workflows list, or a thread switch re-deriving one that was never closed.
 */
export type ArtifactViewSource = "card" | "deep-link" | "report" | "restored"

export interface ArtifactPanelContextValue {
  openArtifact: (artifact: ArtifactEnvelope, opener: HTMLElement | null, name?: string) => void
  activeThreadId: string | null
}

const ArtifactPanelContext = createContext<ArtifactPanelContextValue | null>(null)
export const ArtifactPanelProvider = ArtifactPanelContext.Provider

/** For the inline card. Returns null when rendered outside a panel region (the card's Open then no-ops). */
export function useArtifactPanelActions(): ArtifactPanelContextValue | null {
  return useContext(ArtifactPanelContext)
}

/** What the address names, once it has loaded; the panel shows it instead of its own open state. */
export interface LinkedArtifact {
  artifact: ArtifactEnvelope
  name?: string
  ownThreadId?: string
  /** The address Close navigates to. A report goes back one history entry instead when Kelly has an earlier one, so it closes to where it was opened from; this is then the cold-link target. */
  closeLink?: KellyLink
}

export interface UseArtifactPanelResult {
  openReportArtifact: (artifact: ArtifactEnvelope, name: string) => void
  artifact: ArtifactEnvelope | null
  artifactName: string | null
  isExpanded: boolean
  close: () => void
  toggleExpand: () => void
  /** Esc: collapse an expanded panel back to docked first, else close. */
  handleEscape: () => void
  contextValue: ArtifactPanelContextValue
}

interface OpenArtifactState {
  artifact: ArtifactEnvelope
  threadId: string | null
  newChatSeq: number
  name?: string
  /** Set by openArtifact; a view that comes back via a thread switch reports `restored` instead. */
  source: ArtifactViewSource
}

/**
 * @param activeThreadId  the conversation the panel is scoped to; a change derives the panel closed
 * @param fallbackFocusRef focus target when the opening card is gone on close (e.g. after external deletion)
 * @param newChatSeq       "New chat" counter; a change derives the panel closed whatever it is scoped to
 */
export function useArtifactPanel(activeThreadId: string | null, fallbackFocusRef: FocusFallbackRef, newChatSeq = 0, linked: LinkedArtifact | null = null): UseArtifactPanelResult {
  const [openState, setOpenState] = useState<OpenArtifactState | null>(null)
  const [isLocalExpanded, setIsLocalExpanded] = useState(false)
  const [collapsedLinkedId, setCollapsedLinkedId] = useState<string | null>(null)
  const { link, navigate } = useContext(KellyLinkContext)
  const linkedAddress = kellyLinkOf(linked?.artifact ?? null)
  const isExpanded = linked == null ? isLocalExpanded : deliverableOf(linked.artifact) != null || collapsedLinkedId !== linked.artifact.id
  const openerRef = useRef<HTMLElement | null>(null)
  const analytics = useAnalytics()
  const isDocked = !hasPermanentDrawer(useChatLayout())

  const openArtifact = useCallback((artifact: ArtifactEnvelope, opener: HTMLElement | null, name?: string) => {
    openerRef.current = opener
    const address = kellyLinkOf(artifact)
    if (address != null && navigate(address.type, address.id)) {
      setOpenState(null)
      return
    }
    if (address == null && linked != null) navigate(linked.ownThreadId != null ? "threads" : null, linked.ownThreadId ?? null)
    setIsLocalExpanded(false)
    // An artifact addressed by resource belongs to no thread — stamping the selected one would derive it
    // closed on the next thread switch and report that unrelated id in its view/close/download events.
    const threadId = isResourceAddressedArtifact(artifact) ? null : activeThreadId
    setOpenState({ artifact, threadId, newChatSeq, source: "card", ...(name !== undefined && { name }) })
  }, [activeThreadId, newChatSeq, navigate, linked])

  const openReportArtifact = useCallback((artifact: ArtifactEnvelope, name: string) => {
    const { activeElement } = document
    openerRef.current = activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null
    setIsLocalExpanded(true)
    setOpenState({ artifact, threadId: null, newChatSeq, source: "report", name })
  }, [newChatSeq])

  const close = useCallback(() => {
    if (linkedAddress != null && linked?.ownThreadId != null) navigate("threads", linked.ownThreadId)
    else if (linked?.closeLink != null) {
      const start = kellyStartIndex()
      const index = historyIndex(window.history.state, "idx")
      if (!isDocked && start != null && index != null && index > start) window.history.back()
      else navigate(linked.closeLink.type, linked.closeLink.id)
    } else if (linkedAddress != null) navigate(linkedAddress.type, null)
    setOpenState(null)
    setIsLocalExpanded(false)
    // Return focus to the opening card if it's still in the document; otherwise to a stable fallback so it
    // never drops to <body>.
    const opener = openerRef.current
    openerRef.current = null
    if (opener?.isConnected) opener.focus()
    else fallbackFocusRef.current?.focus()
  }, [fallbackFocusRef, isDocked, linked, linkedAddress, navigate])

  // A panel opened on a brand-new chat (activeThreadId null) belongs to the conversation that adopts a server id
  // on the first turn — that adoption is not a thread switch. Commit the adopted id during render (React-sanctioned)
  // so the panel survives adoption yet a later real switch still derives it closed.
  const isResourceAddressed = openState != null && isResourceAddressedArtifact(openState.artifact)
  if (openState != null && !isResourceAddressed && openState.threadId === null && activeThreadId != null) {
    setOpenState({ ...openState, threadId: activeThreadId })
  }

  // Derived — the panel belongs to the thread it was opened on (see file header). No effect / no setState needed.
  // A resource-addressed artifact has no thread, so no switch derives it closed — only "New chat" does.
  const isCurrent = openState != null && openState.newChatSeq === newChatSeq
    && (isResourceAddressed || openState.threadId === activeThreadId)
  const linkedState = useMemo<OpenArtifactState | null>(
    () => (linked == null ? null : { artifact: linked.artifact, threadId: linked.ownThreadId ?? null, newChatSeq, ...(linked.name !== undefined && { name: linked.name }), source: "deep-link" }),
    [linked, newChatSeq],
  )
  // Minimise keeps the linked item in the docked panel; in full screen the address owns an item it names.
  if (isDocked && linkedState != null && openState !== linkedState) {
    setOpenState(linkedState)
    setIsLocalExpanded(isExpanded)
  }
  const openAddress = kellyLinkOf(openState?.artifact ?? null)
  if (!isDocked && openAddress != null && openAddress.type === linkedAddress?.type && openAddress.id === linkedAddress.id) setOpenState(null)
  const shownState = linkedState ?? (isCurrent ? openState : null)
  const artifact = shownState?.artifact ?? null
  const artifactName = shownState?.name ?? null

  const toggleExpand = useCallback(() => {
    if (linked == null) setIsLocalExpanded((expanded) => !expanded)
    else setCollapsedLinkedId(isExpanded ? linked.artifact.id : null)
    if (shownState == null) return
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_FULLSCREEN_TOGGLED, {
      expanded: !isExpanded,
      artifactId: shownState.artifact.id,
      artifactType: shownState.artifact.type,
      ...(shownState.threadId != null && { threadId: shownState.threadId }),
    })
  }, [shownState, linked, isExpanded, analytics])

  const wasDockedRef = useRef(isDocked)
  useEffect(() => {
    const wasDocked = wasDockedRef.current
    wasDockedRef.current = isDocked
    if (!wasDocked || isDocked || (link?.id != null && link.type !== "threads")) return
    const address = kellyLinkOf(shownState?.artifact ?? null)
    if (address != null) navigate(address.type, address.id, { replace: true })
  }, [isDocked, link, shownState, navigate])

  const handleEscape = useCallback(() => {
    if (isExpanded && (shownState == null || deliverableOf(shownState.artifact) == null)) toggleExpand()
    else close()
  }, [isExpanded, shownState, toggleExpand, close])

  const contextValue = useMemo<ArtifactPanelContextValue>(() => ({ openArtifact, activeThreadId }), [openArtifact, activeThreadId])

  // Both events come off the derived artifact rather than open()/close(): a thread switch, or a card opening
  // over the current artifact, changes what is on screen without either function running. Opening is a view
  // wherever it comes from — including switching back to a thread, which re-shows the artifact that was
  // never cleared, only derived away.
  const openArtifactState = shownState
  const prevOpenArtifactRef = useRef<OpenArtifactState | null>(null)
  // The open-state OBJECT already reported as a click, not its id. Leaving a thread derives the panel closed and
  // returning re-derives the same object, which is the restore this distinguishes; every genuine open (a reclick
  // after close, or re-opening A after B) goes through `openArtifact` and is a fresh object, so it still counts
  // as a click. Written in the effect — refs are write-only during render here (react-hooks/refs).
  const clickReportedStateRef = useRef<OpenArtifactState | null>(null)
  useEffect(() => {
    const previous = prevOpenArtifactRef.current
    prevOpenArtifactRef.current = openArtifactState
    if (previous?.artifact.id === openArtifactState?.artifact.id) {
      // Adopting a server thread id replaces the open-state object with a spread copy while the artifact stays
      // the same. Carry the click report onto the new object, or a later there-and-back would fail the identity
      // check below and report a restore as a fresh click.
      if (clickReportedStateRef.current === previous) clickReportedStateRef.current = openArtifactState
      return
    }
    if (previous != null) {
      analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_CLOSED, {
        artifactId: previous.artifact.id,
        artifactType: previous.artifact.type,
        ...(previous.threadId != null && { threadId: previous.threadId }),
      })
    }
    if (openArtifactState != null) {
      const reportedSource: ArtifactViewSource = openArtifactState.source !== "restored" && clickReportedStateRef.current !== openArtifactState
        ? openArtifactState.source
        : "restored"
      clickReportedStateRef.current = openArtifactState
      analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_VIEWED, {
        artifactId: openArtifactState.artifact.id,
        artifactType: openArtifactState.artifact.type,
        // A view the user asked for and one that reappeared under them are different facts; without this they
        // were one number, and switching threads back and forth inflated it.
        source: reportedSource,
        ...(openArtifactState.threadId != null && { threadId: openArtifactState.threadId }),
      })
    }
  }, [openArtifactState, analytics])

  // Teardown with an artifact still open is a close the transition above never sees — the surface goes away
  // (leaving /kelly-ai, an account switch, a boundary catch) without the derived artifact ever going null.
  // Analytics through a ref so this stays an unmount-only effect rather than re-arming on every render.
  const analyticsRef = useLatest(analytics)
  useEffect(() => () => {
    const previous = prevOpenArtifactRef.current
    if (previous == null) return
    analyticsRef.current.track(AI_TEAMMATE_EVENTS.ARTIFACT_CLOSED, {
      artifactId: previous.artifact.id,
      artifactType: previous.artifact.type,
      ...(previous.threadId != null && { threadId: previous.threadId }),
    })
  }, [analyticsRef])

  return { artifact, artifactName, isExpanded, close, toggleExpand, handleEscape, contextValue, openReportArtifact }
}
